import clsx from 'clsx';
import { Info } from 'lucide-react';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';

import { AnimateModal, ModalOpenProps } from '@/components/ui/modal';

import {
  RIBBON_SHORTCUTS,
  ShortcutSection,
} from '../hooks/ribbonShortcutRegistry';

type ShortcutRow = {
  label: string;
  keys: string;
  tip?: string;
};

type ShortcutGroup = {
  title: ShortcutSection;
  rows: ShortcutRow[];
};

/**
 * Left-to-right order of the sections as they actually appear in the ribbon
 * (see `RibbonSettings`'s layout: MapControlSection | FilterSymbolSection |
 * SizeFilterSection | AddNewElement | Summary/Fittings buttons), so the
 * cheatsheet's tabs line up with where each tool sits on screen.
 */
const SECTION_ORDER: ShortcutSection[] = [
  'Measure',
  'Filters',
  'Size',
  'Add a new Fitting',
  'Panels',
];

/**
 * Derived from `RIBBON_SHORTCUTS` - the same registry `useRibbonShortcuts.ts`
 * binds every ribbon key against - so this list can never drift from what's
 * actually wired up (proposal §5.3). Groups are ordered by `SECTION_ORDER`;
 * rows within a group follow the registry's own property order.
 */
const SHORTCUT_GROUPS: ShortcutGroup[] = Object.values(RIBBON_SHORTCUTS)
  .reduce<ShortcutGroup[]>((groups, entry) => {
    const row = {
      label: entry.label,
      keys: entry.display,
      tip: 'tip' in entry ? entry.tip : undefined,
    };
    const group = groups.find((g) => g.title === entry.section);
    if (group) {
      group.rows.push(row);
    } else {
      groups.push({ title: entry.section, rows: [row] });
    }
    return groups;
  }, [])
  .sort(
    (a, b) => SECTION_ORDER.indexOf(a.title) - SECTION_ORDER.indexOf(b.title),
  );

/**
 * Maps each shortcut group to the `data-onboarding-id` of the ribbon
 * section it corresponds to (see `RibbonSettings`), so the cheatsheet can
 * highlight the matching area on screen while a group's tab is active.
 */
const SECTION_HIGHLIGHT_TARGET: Record<ShortcutSection, string> = {
  Measure: 'map-control',
  Filters: 'filter-symbol',
  Size: 'size-filter-section',
  'Add a new Fitting': 'add-new-element',
  Panels: 'panel-buttons',
};

type HighlightRect = {
  x: number;
  y: number;
  width: number;
  height: number;
};

/**
 * Draws a highlight ring over the ribbon section matching the active
 * shortcut group, portaled to `document.body` so it isn't clipped by the
 * modal's own stacking context.
 */
const ShortcutTargetHighlight = ({ section }: { section: ShortcutSection }) => {
  const [rect, setRect] = useState<HighlightRect | null>(null);

  useEffect(() => {
    const measure = () => {
      const el = document.querySelector(
        `[data-onboarding-id="${SECTION_HIGHLIGHT_TARGET[section]}"]`,
      );
      if (!el) {
        setRect(null);
        return;
      }
      const br = el.getBoundingClientRect();
      const pad = 6;
      setRect({
        x: br.left - pad,
        y: br.top - pad,
        width: br.width + pad * 2,
        height: br.height + pad * 2,
      });
    };

    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [section]);

  if (!rect) return null;

  return createPortal(
    <div
      className="fixed rounded-md pointer-events-none"
      style={{
        top: rect.y,
        left: rect.x,
        width: rect.width,
        height: rect.height,
        border: '2px solid rgba(59, 130, 246, 0.9)',
        boxShadow: '0 0 0 3px rgba(59, 130, 246, 0.25)',
        zIndex: 9999,
        transition: 'all 0.2s ease',
      }}
    />,
    document.body,
  );
};

const KeyBadge = ({ keys }: { keys: string }) => (
  <span className="flex gap-1">
    {keys.split(' + ').map((key) => (
      <kbd
        key={key}
        className={clsx(
          'px-2 py-0.5 rounded border text-xs font-semibold',
          key === 'Esc'
            ? 'border-amber-300 bg-amber-50 text-amber-700'
            : 'border-gray-300 bg-gray-50 color-primary',
        )}
      >
        {key}
      </kbd>
    ))}
  </span>
);

/**
 * Cheatsheet for the ribbon's keyboard shortcuts, tabbed by group
 * (docs/keyboard-shortcuts-proposal.md §7-§8 Phase 3).
 */
export const KeyboardShortcutsModal = ({
  isOpen,
  isOnStage,
  lazyCloseHandler,
}: ModalOpenProps) => {
  const [activeGroup, setActiveGroup] = useState(SHORTCUT_GROUPS[0].title);
  const currentGroup =
    SHORTCUT_GROUPS.find((group) => group.title === activeGroup) ||
    SHORTCUT_GROUPS[0];

  return (
    <>
      {isOpen && <ShortcutTargetHighlight section={activeGroup} />}
      <AnimateModal
        isOpen={isOpen}
        isOnStage={isOnStage}
        lazyCloseHandler={lazyCloseHandler}
        title="Keyboard Shortcuts"
        containerClass="w-auto max-h-full py-4"
      >
        <div className="w-96 mt-4 min-h-[250px]">
          {/* == group tabs == */}
          <div className="flex gap-4 border-b border-gray-200">
            {SHORTCUT_GROUPS.map((group) => (
              <button
                key={group.title}
                type="button"
                className={clsx(
                  'text-sm pb-2 -mb-px border-b-2 focus:outline-none',
                  group.title === activeGroup
                    ? 'border-blue-500 text-blue-600 font-semibold'
                    : 'border-transparent color-blue-gray hover:text-blue-500',
                )}
                onClick={() => setActiveGroup(group.title)}
              >
                {group.title}
              </button>
            ))}
          </div>

          {/* == active group's shortcuts == */}
          <div className="mt-2">
            {currentGroup.rows.map((row) => (
              <div
                key={row.label}
                className="flex items-center justify-between py-1.5 border-b border-gray-100 last:border-b-0"
              >
                <span className="text-sm color-primary flex items-center gap-1">
                  {row.label}
                  {row.tip && (
                    <span title={row.tip} className="cursor-help">
                      <Info className="size-3.5 text-gray-400" />
                    </span>
                  )}
                </span>
                <KeyBadge keys={row.keys} />
              </div>
            ))}
          </div>
        </div>

        {/* button row */}
        <div className="flex justify-end mt-6">
          <button
            className="btn-primary rounded-md px-4 py-2 text-sm"
            onClick={() => lazyCloseHandler()}
          >
            Close
          </button>
        </div>
      </AnimateModal>
    </>
  );
};
